import React from "react";
import { Text, View } from "native-base";
import { ChevronDown, ChevronUp, ShoppingCart, X } from "lucide-react-native";
import {
  Platform,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View as RNView,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from "react-native-reanimated";
import { Portal } from "react-native-alves-portal";
import { useAppContext } from "../Universal/API/Context-API/AppContext";
import type { ResolvedCartItem } from "../Universal/API/Context-API/CartContext";
import { useUserState } from "../Universal/API/Context-API/hooks/useUserState";
import { webFont } from "../Universal/API/API";
import PressableWrapp from "../Universal/Buttons/PressableReact/PressableWrapp";
import {
  getCurrentRouteName,
  rootNavigate,
  subscribeRouteName,
} from "../Navigator/navigationRef";
import { formatCurrency } from "./helpers";
import CartItemCard from "./CartItemCard";

type CartFloatingBarProps = {
  subtotal: number;
  discountedSubtotal: number;
  totalQuantity: number;
  uniqueItemsCount: number;
  resolvedItems: ResolvedCartItem[];
  missingItemIds: string[];
  incrementItem: (id: string | number) => void;
  decrementItem: (id: string | number) => void;
  removeItem: (id: string | number) => void;
  clearCart: () => void;
};

const webCursorPointer = Platform.OS === "web" ? ({ cursor: "pointer" } as any) : null;

const CartFloatingBar: React.FC<CartFloatingBarProps> = ({
  subtotal,
  discountedSubtotal,
  totalQuantity,
  uniqueItemsCount,
  resolvedItems,
  missingItemIds,
  incrementItem,
  decrementItem,
  removeItem,
  clearCart,
}) => {
  const insets = useSafeAreaInsets();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const { isSmallVersion, scaleAll } = useAppContext();
  const userState = useUserState();

  const [expanded, setExpanded] = React.useState(false);
  const currentRouteName = React.useSyncExternalStore(
    subscribeRouteName,
    getCurrentRouteName,
    getCurrentRouteName
  );

  const visibilityProgress = useSharedValue(0);
  const pulseProgress = useSharedValue(1);
  const expandedProgress = useSharedValue(0);

  const shouldHideOnCartScreen = currentRouteName === "Cart";
  const visible = totalQuantity > 0 && !shouldHideOnCartScreen;
  const showDiscountPrices = userState.isUserLoogedInAndShowDiscountPrices;
  const totalPrice = showDiscountPrices ? discountedSubtotal || subtotal : subtotal;

  const barHeight = isSmallVersion ? 68 * scaleAll : 76 * scaleAll;
  const horizontalInset = isSmallVersion ? 12 * scaleAll : 16 * scaleAll;
  const bottomInset = Math.max(insets.bottom + 10 * scaleAll, 16 * scaleAll);
  const availableWidth = Math.max(windowWidth - horizontalInset * 2, 0);
  const barWidth = isSmallVersion
    ? availableWidth
    : Math.min(availableWidth, 520 * scaleAll);
  const panelWidth = isSmallVersion
    ? availableWidth
    : Math.min(availableWidth, 560 * scaleAll);
  const panelBottom = bottomInset + barHeight + 12 * scaleAll;
  const panelMaxHeight = Math.max(
    Math.min(windowHeight - panelBottom - 16 * scaleAll, isSmallVersion ? 520 * scaleAll : 640 * scaleAll),
    260 * scaleAll
  );

  React.useEffect(() => {
    visibilityProgress.value = withTiming(visible ? 1 : 0, {
      duration: 280,
      easing: Easing.out(Easing.cubic),
    });
  }, [visible, visibilityProgress]);

  React.useEffect(() => {
    expandedProgress.value = withTiming(expanded ? 1 : 0, {
      duration: expanded ? 260 : 220,
      easing: Easing.out(Easing.cubic),
    });
  }, [expanded, expandedProgress]);

  React.useEffect(() => {
    if (!visible) {
      setExpanded(false);
      return;
    }

    pulseProgress.value = withSequence(
      withTiming(1.02, {
        duration: 160,
        easing: Easing.out(Easing.cubic),
      }),
      withTiming(1, {
        duration: 180,
        easing: Easing.out(Easing.cubic),
      })
    );
  }, [pulseProgress, totalQuantity, uniqueItemsCount, visible]);

  const barAnimatedStyle = useAnimatedStyle(() => ({
    opacity: visibilityProgress.value,
    transform: [
      {
        translateY: interpolate(visibilityProgress.value, [0, 1], [42, 0]),
      },
      {
        scale: pulseProgress.value,
      },
    ],
  }));

  const backdropAnimatedStyle = useAnimatedStyle(() => ({
    opacity: interpolate(expandedProgress.value, [0, 1], [0, 0.18]),
  }));

  const panelAnimatedStyle = useAnimatedStyle(() => ({
    opacity: expandedProgress.value,
    transform: [
      {
        translateY: interpolate(expandedProgress.value, [0, 1], [28, 0]),
      },
      {
        scale: interpolate(expandedProgress.value, [0, 1], [0.98, 1]),
      },
    ],
  }));

  const handleOpenCart = React.useCallback(() => {
    setExpanded(false);
    rootNavigate("Cart");
  }, []);

  const handleOpenUnit = React.useCallback((id: string) => {
    setExpanded(false);
    rootNavigate("Unit", {
      idUnit: id,
      fromRoute: "Cart",
    });
  }, []);

  return (
    <Portal>
      <RNView
        pointerEvents="box-none"
        style={[
          StyleSheet.absoluteFillObject,
          {
            width: windowWidth,
            height: windowHeight,
            zIndex: 1000,
          },
          Platform.OS === "web"
            ? ({
                position: "fixed",
                left: 0,
                top: 0,
              } as any)
            : null,
        ]}
      >
        <Animated.View
          pointerEvents={expanded ? "auto" : "none"}
          style={[
            StyleSheet.absoluteFillObject,
            { backgroundColor: "#111111" },
            backdropAnimatedStyle,
          ]}
        >
          <PressableWrapp
            onPress={() => setExpanded(false)}
            style={[StyleSheet.absoluteFillObject, webCursorPointer]}
          />
        </Animated.View>

        <Animated.View
          pointerEvents={expanded ? "auto" : "none"}
          style={[
            {
              position: "absolute",
              right: horizontalInset,
              bottom: panelBottom,
              width: panelWidth,
              maxHeight: panelMaxHeight,
            },
            panelAnimatedStyle,
          ]}
        >
          <View
            style={{
              maxHeight: panelMaxHeight,
              borderRadius: 28 * scaleAll,
              backgroundColor: "#FFFFFF",
              borderWidth: 1,
              borderColor: "#E8E8E8",
              shadowColor: "#000",
              shadowOpacity: 0.1,
              shadowRadius: 30,
              shadowOffset: { width: 0, height: 18 },
              elevation: 8,
              overflow: "hidden",
            }}
          >
            <View
              style={{
                paddingHorizontal: 18 * scaleAll,
                paddingTop: 18 * scaleAll,
                paddingBottom: 14 * scaleAll,
                borderBottomWidth: 1,
                borderBottomColor: "#EFEDE8",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <View style={{ flex: 1 }}>
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 11 * scaleAll,
                    color: "#7A746C",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: 0.7,
                    marginBottom: 4 * scaleAll,
                  }}
                >
                  Текущий заказ
                </Text>
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 22 * scaleAll,
                    color: "#111111",
                    fontWeight: "700",
                  }}
                >
                  {uniqueItemsCount} поз. • {totalQuantity} шт.
                </Text>
              </View>

              <PressableWrapp
                onPress={() => setExpanded(false)}
                style={[
                  {
                    width: 42 * scaleAll,
                    height: 42 * scaleAll,
                    borderRadius: 16 * scaleAll,
                    backgroundColor: "#F5F4F1",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  webCursorPointer,
                ]}
              >
                <X size={18 * scaleAll} color="#111111" />
              </PressableWrapp>
            </View>

            {missingItemIds.length > 0 ? (
              <View
                style={{
                  paddingHorizontal: 18 * scaleAll,
                  paddingTop: 12 * scaleAll,
                }}
              >
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 12 * scaleAll,
                    color: "#8A5B13",
                    fontWeight: "600",
                  }}
                >
                  {missingItemIds.length} поз. сейчас не найдены в каталоге и не показаны в списке.
                </Text>
              </View>
            ) : null}

            <ScrollView
              style={{ flexGrow: 0 }}
              contentContainerStyle={{
                paddingHorizontal: 18 * scaleAll,
                paddingTop: 8 * scaleAll,
                paddingBottom: 10 * scaleAll,
              }}
              showsVerticalScrollIndicator={false}
            >
              {resolvedItems.map((item, index) => {
                return (
                  <CartItemCard
                    key={item.id}
                    item={item}
                    scaleAll={scaleAll}
                    isSmallVersion={isSmallVersion}
                    showDiscountPrices={showDiscountPrices}
                    onPressItem={handleOpenUnit}
                    onIncrement={incrementItem}
                    onDecrement={decrementItem}
                    onRemove={removeItem}
                    compact={true}
                    withTopBorder={index !== 0}
                  />
                );
              })}

              {resolvedItems.length === 0 ? (
                <View style={{ paddingVertical: 18 * scaleAll }}>
                  <Text
                    selectable={false}
                    style={{
                      fontFamily: webFont("Inter"),
                      fontSize: 14 * scaleAll,
                      color: "#7A746C",
                      fontWeight: "600",
                    }}
                  >
                    В корзине пока нет доступных для показа товаров.
                  </Text>
                </View>
              ) : null}
            </ScrollView>

            <View
              style={{
                borderTopWidth: 1,
                borderTopColor: "#EFEDE8",
                paddingHorizontal: 18 * scaleAll,
                paddingTop: 14 * scaleAll,
                paddingBottom: 18 * scaleAll,
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12 * scaleAll,
              }}
            >
              <View style={{ flex: 1 }}>
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 11 * scaleAll,
                    color: "#7A746C",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: 0.7,
                    marginBottom: 4 * scaleAll,
                  }}
                >
                  Итого
                </Text>
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 22 * scaleAll,
                    color: "#111111",
                    fontWeight: "700",
                  }}
                >
                  {formatCurrency(totalPrice)} ₽
                </Text>
              </View>

              <View
                style={{
                  flexDirection: isSmallVersion ? "column" : "row",
                  alignItems: "stretch",
                  gap: 10 * scaleAll,
                }}
              >
                <PressableWrapp
                  onPress={clearCart}
                  style={[
                    {
                      minHeight: 48 * scaleAll,
                      borderRadius: 18 * scaleAll,
                      backgroundColor: "#111111",
                      paddingHorizontal: 18 * scaleAll,
                      alignItems: "center",
                      justifyContent: "center",
                    },
                    webCursorPointer,
                  ]}
                >
                  <Text
                    selectable={false}
                    style={{
                      fontFamily: webFont("Inter"),
                      fontSize: 13 * scaleAll,
                      color: "#FFFFFF",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Очистить
                  </Text>
                </PressableWrapp>

                <PressableWrapp
                  onPress={handleOpenCart}
                  style={[
                    {
                      minHeight: 48 * scaleAll,
                      borderRadius: 18 * scaleAll,
                      backgroundColor: "#F5F4F1",
                      borderWidth: 1,
                      borderColor: "#E6E1D8",
                      paddingHorizontal: 18 * scaleAll,
                      alignItems: "center",
                      justifyContent: "center",
                    },
                    webCursorPointer,
                  ]}
                >
                  <Text
                    selectable={false}
                    style={{
                      fontFamily: webFont("Inter"),
                      fontSize: 13 * scaleAll,
                      color: "#111111",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Открыть корзину
                  </Text>
                </PressableWrapp>
              </View>
            </View>
          </View>
        </Animated.View>

        <Animated.View
          pointerEvents={visible ? "auto" : "none"}
          style={[
            {
              position: "absolute",
              right: horizontalInset,
              bottom: bottomInset,
              width: barWidth,
            },
            barAnimatedStyle,
          ]}
        >
          <View
            style={[
              {
                width: "100%",
                minHeight: barHeight,
                borderRadius: 26 * scaleAll,
                backgroundColor: "#FFFFFF",
                borderWidth: 1,
                borderColor: "#E8E8E8",
                paddingHorizontal: 14 * scaleAll,
                paddingVertical: 12 * scaleAll,
                shadowColor: "#000",
                shadowOpacity: 0.08,
                shadowRadius: 24,
                shadowOffset: { width: 0, height: 14 },
                elevation: 6,
                flexDirection: "row",
                alignItems: "center",
              },
            ]}
          >
            <PressableWrapp
              onPress={handleOpenCart}
              style={[
                {
                  width: 44 * scaleAll,
                  height: 44 * scaleAll,
                  borderRadius: 16 * scaleAll,
                  backgroundColor: "#111111",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 12 * scaleAll,
                  flexShrink: 0,
                },
                webCursorPointer,
              ]}
            >
              <ShoppingCart size={20} strokeWidth={2.2} color="#FFFFFF" />
            </PressableWrapp>

            <PressableWrapp
              onPress={() => setExpanded((prev) => !prev)}
              style={{
                flex: 1,
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <View style={{ flex: 1, paddingRight: 10 * scaleAll }}>
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 11 * scaleAll,
                    color: "#7A746C",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: 0.7,
                    marginBottom: 4 * scaleAll,
                  }}
                >
                  Текущий заказ
                </Text>
                <Text
                  selectable={false}
                  numberOfLines={1}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: isSmallVersion ? 13 * scaleAll : 14 * scaleAll,
                    color: "#111111",
                    fontWeight: "600",
                  }}
                >
                  {uniqueItemsCount} поз. • {totalQuantity} шт.
                </Text>
              </View>

              <View style={{ alignItems: "flex-end", flexShrink: 0, marginRight: 10 * scaleAll }}>
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 11 * scaleAll,
                    color: "#7A746C",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: 0.7,
                    marginBottom: 4 * scaleAll,
                  }}
                >
                  Сумма
                </Text>
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: isSmallVersion ? 18 * scaleAll : 20 * scaleAll,
                    color: "#111111",
                    fontWeight: "700",
                  }}
                >
                  {formatCurrency(totalPrice)} ₽
                </Text>
              </View>

              <View
                style={{
                  width: 36 * scaleAll,
                  height: 36 * scaleAll,
                  borderRadius: 14 * scaleAll,
                  backgroundColor: "#F5F4F1",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {expanded ? (
                  <ChevronDown size={18 * scaleAll} color="#111111" />
                ) : (
                  <ChevronUp size={18 * scaleAll} color="#111111" />
                )}
              </View>
            </PressableWrapp>
          </View>
        </Animated.View>
      </RNView>
    </Portal>
  );
};

export default React.memo(CartFloatingBar);
